
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Heart, User } from "lucide-react";
import { useCompleteProfile } from "@/hooks/useCompleteProfile";
import { ProfilePhotoSection } from "@/components/profile/ProfilePhotoSection";
import ProfileBasicInfo from "@/components/profile/ProfileBasicInfo";
import { ProfileHierarchySection } from "@/components/profile/ProfileHierarchySection";
import { ProfileInterests } from "@/components/profile/ProfileInterests";
import { ProfileAgeRange } from "@/components/profile/ProfileAgeRange";
import { ProfileAddressSection } from "@/components/profile/ProfileAddressSection";
import AdPanel from "@/components/ads/AdPanel";
import { AskJudiAnalyzer } from "@/components/profile/AskJudiAnalyzer";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface CompleteProfileProps {
  refetchProfileCompletion?: () => Promise<void>;
}

const CompleteProfile = ({ refetchProfileCompletion }: CompleteProfileProps) => {
  const {
    user,
    loading,
    loadingProfile,
    isFirstTime,
    photos,
    setPhotos,
    privateInterests,
    setPrivateInterests,
    publicInterests,
    setPublicInterests,
    formData,
    handleInputChange,
    handleSubmit,
    isFormValid
  } = useCompleteProfile(refetchProfileCompletion);

  const handleAIBioGenerated = (bio: string) => {
    handleInputChange('bio', bio);
  };

  const handleBioChange = (bio: string) => {
    handleInputChange('bio', bio);
  };

  const handleButtonClick = (e: React.FormEvent) => {
    console.log('🔥 Button clicked! Form valid:', isFormValid, 'Loading:', loading);
    handleSubmit(e);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-ice-white">Please sign in to complete your profile.</p>
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="small" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-carbon">
      <AdPanel />
      
      <RightSideMenu />
      
      <div className="p-4 pb-24 relative">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-ice-white mb-2">
              {isFirstTime ? "Complete profile" : "Edit profile"}
            </h1>
          </div>

          <ProfilePhotoSection photos={photos} onPhotosChange={setPhotos} />

          <Card className="mb-6 bg-carbon border-ice-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ice-white">
                <User className="w-5 h-5 text-neon-red" />
                {isFirstTime ? "Complete your information" : "Edit your information"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleButtonClick} className="space-y-6">
                <ProfileBasicInfo
                  formData={formData}
                  onInputChange={handleInputChange}
                  loading={loading}
                />
              </form>
            </CardContent>
          </Card>

          <Card className="mb-6 bg-carbon border-ice-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ice-white">
                <User className="w-5 h-5 text-neon-red" />
                Hierarchy Type
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProfileHierarchySection
                hierarchy={formData.hierarchy}
                onHierarchyChange={(value) => handleInputChange('hierarchy', value)}
                loading={loading}
              />
            </CardContent>
          </Card>

          <Card className="mb-6 bg-carbon border-ice-white/10">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-ice-white">
                <Heart className="w-5 h-5 text-neon-red" />
                Interests & Preferences
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleButtonClick} className="space-y-6">
                <ProfileInterests
                  publicInterests={publicInterests}
                  onPublicInterestsChange={setPublicInterests}
                  privateInterests={privateInterests}
                  onPrivateInterestsChange={setPrivateInterests}
                  loading={loading}
                />

                <ProfileAgeRange
                  formData={formData}
                  onInputChange={handleInputChange}
                  loading={loading}
                />

                <ProfileAddressSection
                  formData={formData}
                  onInputChange={handleInputChange}
                  loading={loading}
                />

                <Button 
                  type="submit" 
                  className="w-full bg-neon-red hover:bg-neon-red/80 text-ice-white" 
                  disabled={loading || !isFormValid}
                  onClick={() => console.log('🎯 Button onClick triggered! Valid:', isFormValid, 'Disabled:', loading || !isFormValid)}
                >
                  {loading ? "Completing Profile..." : "Complete Profile & Start Dating!"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="mb-6">
            <div className="text-center mb-4">
              <h2 className="text-xl font-semibold text-ice-white mb-2">Ask Judi - AI Profile Analysis</h2>
              <p className="text-ice-white/70 text-sm">Let Judi help you create the perfect profile with her AI insights</p>
            </div>
            
            <AskJudiAnalyzer
              onBioGenerated={handleAIBioGenerated}
              onBioChange={handleBioChange}
              formData={formData}
              photos={photos}
              publicInterests={publicInterests}
              bio={formData.bio}
            />
          </div>
        </div>
      </div>
      
      <div className="h-20"></div>
    </div>
  );
};

export default CompleteProfile;
